import React, { useState } from 'react';
import TagsDropdown from '../../../FlowchartView/TaskNode/TagsDropdown';
import NestedSprintIndicator from '../../../FlowchartView/TaskNode/components/NestedSprintIndicator';
import DueDateDisplay from '../DueDateDisplay';
import CustomFieldsDisplay from '../CustomFieldsDisplay';
import { CardHeader, CardBadges, CardToolbar } from './components';
import AttachedDocsOnCard from './components/AttachedDocsOnCard';
import DropdownRows from './components/DropdownRows';
import DecisionOptionsModal from '../../../FlowchartView/TaskNode/DecisionOptionsModal';
import { useCardDisplaySettings } from './hooks/useCardDisplaySettings';
import { useDecisionState } from './hooks/useDecisionState';
import RemindMeRow from '../RemindMeRow';
import WaitForFields from './components/WaitForFields';
import { isWaitNode } from '../../../../../utils/waitFor';
import ConditionsDropdown from '../../ConditionsDropdown';
import { getEffectiveConditionsV2 } from '../../ConditionsDropdown/utils/conditionHelpers';
import { collectWaitHints, isWaitStatusReason } from '../../ConditionsDropdown/utils/conditionWait';
import { boardTasksLive } from '../../../BoardView/boardTasksLive';
import { ClockIcon } from '../DueDateDisplay/icons';
import './styles/index.css';

// Fixed class prefix - this component always uses the same classes
const CLASS_PREFIX = 'mission-card';

/**
 * MissionCardBody Component
 * 
 * The COMPLETE visual content of a mission card, shared between:
 * - TaskNode (Flow view)
 * - KanbanCard (Kanban view)
 * - Future views
 * 
 * This component renders ALL card content in a canonical order:
 * 1. Category color bar
 * 2. Blocked/Archived badges
 * 3. Header (icon + editable name)
 * 4. Blocked reason
 * 5. Status & Priority row
 * 6. Tags row
 * 7. Date fields
 * 8. Nested sprint indicator
 * 9. Toolbar
 * 
 * The wrapper component (TaskNode/KanbanCard) handles:
 * - Outer container and positioning
 * - Connection handles (Flow only)
 * - Resize handles (Flow only)
 * - Drag systems
 * - Popovers (Flow only)
 */
const MissionCardBody = ({
  // Task data
  task,
  category, // Pre-resolved primary tag/category object
  allTags = [],
  allTasks = [], // All tasks in campaign (for dependency dropdown)
  statuses = [],
  nestedSprint = null,
  nestedSprints = [], // All nested sprints (for conditions modal + est. time rollup)
  allSprints = [],
  
  // State flags
  isBlocked = false,
  isLocked = false,
  isExcluded = false,
  lockReason = '',
  blockingDependencies = [],
  parentMissionName = '',
  parentCampaignName = '',
  parentMissionId = null,
  parentSprintId = null,
  onNavigateToParentMission,
  
  // Display settings
  cardDisplaySettings = {},
  showCategoryBar = true, // Whether to render the category color bar at top
  isTemplate = false, // Whether editing a template (affects date field behavior)
  
  // Name editing (can be controlled externally)
  isEditingTitle = false,
  onFinishTitleEdit,
  
  // Callbacks
  onRename,
  onIconChange,
  onStatusChange,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  onOpenDetails,
  onOpenAttachments,
  onOpenNestedSprint,
  onRemoveNestedSprint,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync, // Callback to toggle auto-sync mode for nested sprint tasks
  
  
  // Date callbacks
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  
  // Priority callback
  onPriorityChange,
  
  // Difficulty callback
  onDifficultyChange,
  
  // Archive callbacks
  onArchiveTask,
  onUnarchiveTask,
  
  // Delete callback
  onDeleteTask,
  
  // Conditions callback (unlock rules)
  onConditionsChange,
  onConditionsDialogOpen,
  onConditionsDialogClose,
  
  // Go to dependency (for warning modal)
  onGoToDependency,
  
  // Submit deliverable (for warning modal)
  onSubmitDeliverable,
  
  // Edit deliverables (for conditions modal)
  onEditDeliverables,
  
  // Clear deliverable (for conditions modal)
  onClearDeliverable,
  
  // View navigation (for "Open in view" button)
  campaignId,
  currentView,
  customViews = [],
  activeCustomViewId = null,
  
  // ═══════════════════════════════════════════════════════════════
  // CUSTOM FIELDS (campaign-level field definitions + value change handler)
  // ═══════════════════════════════════════════════════════════════
  customFields = [],
  onCustomFieldValueChange,
  
  // ═══════════════════════════════════════════════════════════════
  // DECISION NODE PROPS (only used when task.nodeType === 'decision')
  // ═══════════════════════════════════════════════════════════════
  onAnswerChange,
  onOptionsChange,
  // For highlighting which branch is being hovered during connection drag
  hoveredBranch = null,
}) => {
  // ═══════════════════════════════════════════════════════════════
  // HOOKS - Display Settings & Decision State
  // ═══════════════════════════════════════════════════════════════
  
  const isWait = isWaitNode(task);
  const displaySettings = useCardDisplaySettings(cardDisplaySettings, task.nodeType === 'decision');
  
  const {
    isDecision,
    options,
    answer,
    showOptionsModal,
    setShowOptionsModal,
    handleAnswerClick,
    handleOptionsChange,
  } = useDecisionState(task, onAnswerChange, onOptionsChange);
  const [isWaitConditionsOpen, setIsWaitConditionsOpen] = useState(false);
  
  // Custom fields - show if there are any custom fields defined for this campaign
  // Individual field visibility is handled inside CustomFieldsDisplay based on cardDisplaySettings
  const showCustomFields = customFields.length > 0;

  // For tasks with nested campaigns, use the nested campaign's icon instead of task.icon
  // This creates a bidirectional sync: changing the icon here changes the nested campaign's icon
  const effectiveIcon = nestedSprint?.icon ?? task.icon;
  const hasIcon = !!effectiveIcon && displaySettings.showMissionIcon;
  const waitHints = collectWaitHints(getEffectiveConditionsV2(task), boardTasksLive.current || allTasks);
  const waitInStatus = isWaitStatusReason(lockReason);
  const waitChips = !isLocked || waitInStatus ? [] : waitHints;

  const openWaitConditions = (e) => {
    e.stopPropagation();
    if (!onConditionsChange) return;
    onConditionsDialogOpen?.();
    setIsWaitConditionsOpen(true);
  };

  const closeWaitConditions = () => {
    setIsWaitConditionsOpen(false);
    onConditionsDialogClose?.();
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <>
      {/* 1. Category color bar - wrapper can disable via showCategoryBar={false} if it handles it via ::before */}
      {showCategoryBar && category && <div className={`${CLASS_PREFIX}__category-bar`} />}
      
      {isDecision && (
        <div className={`${CLASS_PREFIX}__decision-badge`}>
          <span className={`${CLASS_PREFIX}__decision-badge-icon`} aria-hidden="true" />
          <span className={`${CLASS_PREFIX}__decision-badge-text`}>Decision</span>
        </div>
      )}
      
      {/* 2b. Blocked/Archived badges */}
      <CardBadges
        task={task}
        isBlocked={isBlocked}
        showBlockedIndicator={displaySettings.showBlockedIndicator}
      />
      
      {/* 3. Header - Icon + Name + Lock/Excluded indicator */}
      <CardHeader
        task={task}
        effectiveIcon={effectiveIcon}
        hasIcon={hasIcon}
        category={category}
        isEditingTitle={isEditingTitle}
        isLocked={isLocked}
        isExcluded={isExcluded}
        isWait={isWait}
        lockReason={lockReason}
        onFinishTitleEdit={onFinishTitleEdit}
        onRename={onRename}
        onIconChange={onIconChange}
      />
      
      {/* 3b. Decision Answer Toggle (for decision nodes) */}
      {isDecision && (
        <div className={`${CLASS_PREFIX}__decision-answer-row`}>
          {isLocked ? (
            <div className={`${CLASS_PREFIX}__decision-locked`} title={lockReason || 'Decision is locked'}>
              <span className={`${CLASS_PREFIX}__decision-locked-icon`}>🔒</span>
              <span className={`${CLASS_PREFIX}__decision-locked-text`}>Locked</span>
            </div>
          ) : (
            <div className={`${CLASS_PREFIX}__decision-toggle`}>
              {options.map((option) => (
                <button
                  key={option.id}
                  className={`${CLASS_PREFIX}__decision-btn ${answer === option.id ? `${CLASS_PREFIX}__decision-btn--active` : ''} ${hoveredBranch === option.id ? `${CLASS_PREFIX}__decision-btn--hovered` : ''}`}
                  style={{ '--option-color': option.color }}
                  onClick={handleAnswerClick(option.id)}
                  onMouseDown={(e) => e.stopPropagation()}
                  title={option.label}
                >
                  {option.label}
                </button>
              ))}
              {/* Add/Edit choices button */}
              <button
                className={`${CLASS_PREFIX}__decision-add-btn`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOptionsModal(true);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                title="Add or edit choices"
              >
                +
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* 4. Blocked reason */}
      {displaySettings.showBlockedIndicator && isBlocked && task.blockedReason && (
        <div className={`${CLASS_PREFIX}__blocked-reason`}>
          {task.blockedReason}
        </div>
      )}

      {waitChips.length > 0 && (
        <div className={`${CLASS_PREFIX}__wait-hints`}>
          {waitChips.map((hint) => (
            onConditionsChange ? (
              <button
                key={hint}
                type="button"
                className={`${CLASS_PREFIX}__wait-hint ${CLASS_PREFIX}__wait-hint--button`}
                title={`${hint} — edit wait`}
                onClick={openWaitConditions}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <ClockIcon size={13} />
                <span>{hint}</span>
              </button>
            ) : (
              <div key={hint} className={`${CLASS_PREFIX}__wait-hint`} title={hint}>
                <ClockIcon size={13} />
                <span>{hint}</span>
              </div>
            )
          ))}
        </div>
      )}
      
      {isWait && (
        <WaitForFields task={task} classPrefix={CLASS_PREFIX} />
      )}

      {/* 5. Status, Priority, Difficulty & Custom Select Fields rows (2 per row max) */}
      <DropdownRows
        task={task}
        showStatusDropdown={isWait ? false : displaySettings.showStatusDropdown}
        showPriorityDropdown={displaySettings.showPriorityDropdown}
        showDifficultyDropdown={displaySettings.showDifficultyDropdown}
        isLocked={isLocked}
        lockReason={lockReason}
        blockingDependencies={blockingDependencies}
        parentMissionName={parentMissionName}
        parentCampaignName={parentCampaignName}
        onGoToParentMission={
          parentMissionId && parentSprintId && onNavigateToParentMission
            ? () => onNavigateToParentMission({ parentSprintId, parentMissionId })
            : undefined
        }
        nestedSprint={nestedSprint}
        onStatusChange={onStatusChange}
        onSetStatusAutoSync={onSetStatusAutoSync}
        statuses={statuses}
        allTasks={allTasks}
        onAddStatus={onAddStatus}
        onDeleteStatus={onDeleteStatus}
        onRenameStatus={onRenameStatus}
        onGoToDependency={onGoToDependency}
        onSubmitDeliverable={onSubmitDeliverable}
        onOpenDetails={onOpenDetails}
        onPriorityChange={onPriorityChange}
        onDifficultyChange={onDifficultyChange}
        customFields={customFields}
        cardDisplaySettings={cardDisplaySettings}
        onCustomFieldValueChange={onCustomFieldValueChange}
      />

      {/* 6. Tags row */}
      {displaySettings.showCategoryDropdown && (
        <div className={`${CLASS_PREFIX}__tags-row-wrapper`}>
          <TagsDropdown
            taskId={task.id}
            task={task}
            allTags={allTags}
            onAddTag={onAddTag}
            onRemoveTag={onRemoveTag}
            onSetPrimaryTag={onSetPrimaryTag}
            onRenameTag={onRenameTag}
            onDeleteTag={onDeleteTag}
          />
        </div>
      )}

      {/* 7. Date fields row */}
      {displaySettings.showAnyDateField && (
        <DueDateDisplay
          task={task}
          showDueDate={displaySettings.showDueDate}
          showEstimatedDuration={displaySettings.showEstimatedDuration}
          showCreatedAt={displaySettings.showCreatedAt}
          showStartedAt={displaySettings.showStartedAt}
          showCompletedAt={displaySettings.showCompletedAt}
          showUpdatedAt={displaySettings.showUpdatedAt}
          onDueDateChange={onDueDateChange}
          onEstimatedDurationMinutesChange={onEstimatedDurationMinutesChange}
          onCreatedAtChange={onCreatedAtChange}
          onStartedAtChange={onStartedAtChange}
          onCompletedAtChange={onCompletedAtChange}
          onUpdatedAtChange={onUpdatedAtChange}
          classPrefix={CLASS_PREFIX}
          isTemplate={isTemplate}
          statuses={statuses}
          nestedSprints={nestedSprints}
          allSprints={allSprints}
        />
      )}

      {!isTemplate && onRemindersChange && (
        <RemindMeRow
          task={task}
          onRemindersChange={isWait ? (taskId, next) => {
            const tagged = (next || []).map((reminder) => (
              reminder?.trigger === 'at_time' ? { ...reminder, hold: true } : reminder
            ));
            onRemindersChange(taskId, tagged);
          } : onRemindersChange}
          isTemplate={isTemplate}
          isLocked={isWait ? false : isLocked}
          prompt={isWait ? 'Remind me in' : undefined}
          classPrefix={CLASS_PREFIX}
        />
      )}

      {/* 8. Custom Fields */}
      {showCustomFields && (
        <CustomFieldsDisplay
          task={task}
          customFields={customFields}
          cardDisplaySettings={cardDisplaySettings}
          onFieldValueChange={onCustomFieldValueChange}
          classPrefix={CLASS_PREFIX}
        />
      )}

      {displaySettings.showAttachedDocs && task.attachedDocs?.length > 0 && (
        <AttachedDocsOnCard
          task={task}
          onOpenDoc={(docId) => onOpenDetails?.(task.id, { docId })}
        />
      )}

      {/* 9. Nested Sprint Indicator */}
      {(displaySettings.showNestedCampaign || task.creatingCampaign) && (
        <NestedSprintIndicator
          nestedSprint={nestedSprint}
          creating={!!task.creatingCampaign}
          onOpenNestedSprint={onOpenNestedSprint}
          onRemoveNestedSprint={onRemoveNestedSprint}
        />
      )}

      {/* 10. Toolbar */}
      <CardToolbar
        task={task}
        campaignId={campaignId}
        showAttachmentsButton={displaySettings.showAttachmentsButton}
        showArchiveButton={displaySettings.showArchiveButton}
        showDeleteButton={displaySettings.showDeleteButton}
        showOpenInViewButton={displaySettings.showOpenInViewButton}
        showViewMissionButton={displaySettings.showDetailsButton}
        attachmentsCount={task.attachmentCount || 0}
        onOpenAttachments={onOpenAttachments}
        onArchiveTask={onArchiveTask}
        onUnarchiveTask={onUnarchiveTask}
        onDeleteTask={onDeleteTask}
        onOpenDetails={onOpenDetails}
        currentView={currentView}
        customViews={customViews}
        activeCustomViewId={activeCustomViewId}
      />
      
      {/* Decision Options Modal */}
      {isDecision && (
        <DecisionOptionsModal
          isOpen={showOptionsModal}
          options={options}
          onSave={handleOptionsChange}
          onClose={() => setShowOptionsModal(false)}
        />
      )}

      {onConditionsChange && (
        <ConditionsDropdown
          task={task}
          allTasks={allTasks}
          onConditionsChange={onConditionsChange}
          isOpen={isWaitConditionsOpen}
          onClose={closeWaitConditions}
        />
      )}
    </>
  );
};

export default MissionCardBody;
